/**
 * YouTube URL parse + nocookie embed. Kept out of `loom_video.ts`
 * (file-size cap). Lessons and knowledge-page video blocks both use this.
 */

export const YOUTUBE_PROVIDER = "youtube" as const;

const YOUTUBE_HOSTS: ReadonlySet<string> = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "youtu.be",
  "www.youtu.be",
  "youtube-nocookie.com",
  "www.youtube-nocookie.com",
]);

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;
const YOUTUBE_PATH_ID_RE =
  /^\/(?:embed|shorts|live|v)\/([A-Za-z0-9_-]{11})\/?$/;
const YOUTUBE_MARKDOWN_RE = /^\[YouTube-Video\]\(([^)]+)\)$/i;

export interface ParsedYouTubeVideo {
  readonly video_id: string;
  readonly embed_query: string;
}

/** True when the value is a canonical 11-character YouTube video id. */
export function isYouTubeVideoId(value: string): boolean {
  return YOUTUBE_ID_RE.test(value);
}

/** Parse `90`, `90s`, `1m30s`, `1h2m3s` into whole seconds. */
export function parseYouTubeTimestamp(raw: string): number | undefined {
  const trimmed: string = raw.trim();
  if (trimmed.length === 0) return undefined;
  if (/^\d+$/.test(trimmed)) {
    const n: number = Number(trimmed);
    return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
  }
  const match: RegExpExecArray | null =
    /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i.exec(trimmed);
  if (
    match === null ||
    (match[1] === undefined && match[2] === undefined && match[3] === undefined)
  ) {
    return undefined;
  }
  const hours: number = Number(match[1] ?? 0);
  const minutes: number = Number(match[2] ?? 0);
  const seconds: number = Number(match[3] ?? 0);
  return hours * 3600 + minutes * 60 + seconds;
}

function hashStart(hash: string): string | null {
  const body: string = hash.startsWith("#") ? hash.slice(1) : hash;
  if (body.startsWith("t=")) {
    try {
      return decodeURIComponent(body.slice(2));
    } catch {
      return body.slice(2);
    }
  }
  return new URLSearchParams(body).get("t");
}

function startQuery(seconds: number): string {
  return seconds > 0 ? `start=${seconds}` : "";
}

function videoIdFromUrl(url: URL): string | undefined {
  const host: string = url.hostname.toLowerCase();
  if (!YOUTUBE_HOSTS.has(host)) return undefined;
  if (host === "youtu.be" || host === "www.youtu.be") {
    const id: string | undefined = url.pathname.split("/").filter(Boolean)[0];
    return id !== undefined && isYouTubeVideoId(id) ? id : undefined;
  }
  const pathMatch: RegExpExecArray | null = YOUTUBE_PATH_ID_RE.exec(
    url.pathname,
  );
  if (pathMatch?.[1] !== undefined) return pathMatch[1];
  const v: string | null = url.searchParams.get("v");
  if (v !== null && isYouTubeVideoId(v)) return v;
  return undefined;
}

/** Parse a YouTube watch / share / embed / Shorts URL. */
export function parseYouTubeUrl(raw: string): ParsedYouTubeVideo | null {
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const videoId: string | undefined = videoIdFromUrl(url);
  if (videoId === undefined) return null;
  const token: string | null =
    url.searchParams.get("start") ??
    url.searchParams.get("t") ??
    hashStart(url.hash);
  const start: number | undefined =
    token === null ? undefined : parseYouTubeTimestamp(token);
  return {
    video_id: videoId,
    embed_query: start === undefined ? "" : startQuery(start),
  };
}

/** Bare URL or canonical `[YouTube-Video](url)` line. */
export function parseYouTubeLine(value: string): ParsedYouTubeVideo | null {
  const trimmed: string = value.trim();
  if (trimmed.length === 0 || trimmed.includes("\n")) return null;
  const markdown: RegExpExecArray | null = YOUTUBE_MARKDOWN_RE.exec(trimmed);
  return parseYouTubeUrl(markdown?.[1] ?? trimmed);
}

/** Allowlisted nocookie embed URL (`enablejsapi` for lesson progress). */
export function youtubeEmbedUrl(
  videoId: string,
  embedQuery = "",
  pageOrigin?: string,
): string {
  if (!isYouTubeVideoId(videoId)) return "about:blank";
  const params = new URLSearchParams();
  params.set("enablejsapi", "1");
  params.set("rel", "0");
  params.set("playsinline", "1");
  if (pageOrigin !== undefined && pageOrigin.length > 0) {
    params.set("origin", pageOrigin);
  }
  const stored = new URLSearchParams(embedQuery.replace(/^\?/, ""));
  const startRaw: string | null = stored.get("start");
  if (startRaw !== null) {
    const start: number | undefined = parseYouTubeTimestamp(startRaw);
    if (start !== undefined && start > 0) params.set("start", String(start));
  }
  return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoId)}?${params.toString()}`;
}

/** Canonical watch URL. Uses `&start=` because `v=` is already the query. */
export function youtubeShareUrl(videoId: string, embedQuery = ""): string {
  if (!isYouTubeVideoId(videoId)) return "";
  const stored = new URLSearchParams(embedQuery.replace(/^\?/, ""));
  const startRaw: string | null = stored.get("start");
  const start: number | undefined =
    startRaw === null ? undefined : parseYouTubeTimestamp(startRaw);
  const startSuffix: string =
    start !== undefined && start > 0 ? `&start=${String(start)}` : "";
  return `https://www.youtube.com/watch?v=${encodeURIComponent(videoId)}${startSuffix}`;
}
